import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { P, isMatching } from 'ts-pattern'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import { useAnalytics } from '@/context/AnalyticsContext'
import type { AppliedCoupon } from '@/hooks/useCoupon'
import { useOtpFlow } from '@/hooks/useOtpFlow'
import { reportError } from '@/services/errorReporting'
import { getPathWithParams } from '@/utils/url'
import type { PermanentDiscountData, PriceData } from '@flows-ui/services/QuoteDataContext.types'
import {
  buildProductAddedPayload,
  extractTrackingFlowValues,
  getPremiumIntervalFromValues,
  getTariffLine,
  permanentDiscountTrackingSchema,
} from '@flows-ui/utils/tracking'
import { EVENTS } from '@flows/configs/analytics/constants'
import type { FlowValues } from '@flows/core/types/stepTypes'

import type {
  FlowStepFlags,
  FlowStepPageConfig,
  FlowStepPageProps,
  FlowStepSubmitInput,
} from './types'

const storeReferralResult = ({
  referralUrl,
  referralRewards,
}: {
  success: boolean
  referralUrl?: string
  referralRewards?: unknown
}) => {
  if (referralUrl) {
    try {
      localStorage.setItem('gs_referralUrl', referralUrl)
    } catch (error: unknown) {
      reportError(error, { context: '[purchase] storing referral url failed' })
    }
  }
  if (referralRewards) {
    try {
      localStorage.setItem('gs_referralRewards', JSON.stringify(referralRewards))
    } catch (error: unknown) {
      reportError(error, { context: '[purchase] storing referral rewards failed' })
    }
  }
}

export const useFlowSubmit = ({
  config,
  props,
  language,
  market,
  appliedCoupon,
  flowValues,
  latestPriceData,
  onCouponDropped,
  permanentDiscount,
  stepFlags,
  whitelistedParams,
}: {
  appliedCoupon: AppliedCoupon | null
  onCouponDropped: () => void
  config: FlowStepPageConfig
  flowValues: FlowValues
  language: Language
  latestPriceData: PriceData | undefined
  market: SupportedMarketKey
  permanentDiscount: PermanentDiscountData | null
  props: FlowStepPageProps
  stepFlags: FlowStepFlags | undefined
  whitelistedParams: string
}) => {
  const { t } = useTranslation()
  const { track } = useAnalytics()
  const { push } = useRouter()
  const [backBtnPressed, setBackBtnPressed] = useState(false)

  const utils = config.useUtils()
  const mutation = config.router.submit.useMutation({
    onSuccess: () => {
      void utils.invalidate()
    },
  })

  const checkEmailMutation = config.router.checkEmail.useMutation()
  const verifyOtpMutation = config.router.verifyOtp.useMutation()
  const pendingSubmitValues = useRef<Record<string, unknown> | null>(null)
  const otpSentTo = useRef<string | null>(null)
  const [airpodsNoticeVisible, setAirpodsNoticeVisible] = useState(false)

  const submitStep = (values: Record<string, unknown>) =>
    mutation.mutate({
      values,
      stepName: props.stepName,
      language,
      market,
      firstStepName: props.firstStepName,
    } as FlowStepSubmitInput)

  const otp = useOtpFlow({
    checkEmailMutation,
    verifyOtpMutation,
    currentStepName: props.stepName,
    errorMessages: {
      invalidCode: t('sharedFlows_common:otpLogin.error'),
      resendError: t('sharedFlows_common:otpLogin.resendError'),
    },
    onVerified: (result) => {
      otpSentTo.current = null
      permanentDiscount?.revokePermanentDiscount()
      if (result.couponDropped) {
        onCouponDropped()
        setAirpodsNoticeVisible(true)
        return
      }
      submitPendingValues()
    },
  })

  const submitPendingValues = () => {
    if (pendingSubmitValues.current) {
      submitStep(pendingSubmitValues.current)
      pendingSubmitValues.current = null
    }
  }

  const airpodsNotice = {
    visible: airpodsNoticeVisible,
    onContinue: () => {
      setAirpodsNoticeVisible(false)
      submitPendingValues()
    },
  }

  const [purchasing, setPurchasing] = useState(false)
  const [purchaseError, setPurchaseError] = useState<string | null>(null)
  const createContractMutation = config.router.registerAndCreateContract.useMutation()

  /**
   * The contract is created before the payment step is submitted, so a failure
   * keeps the user on the step with an error instead of advancing.
   */
  const runPurchase = async () => {
    if (otpSentTo.current !== null && otpSentTo.current === flowValues.email) {
      otp.requestCode(otpSentTo.current)
      return false
    }
    setPurchasing(true)
    setPurchaseError(null)
    const discountToken = permanentDiscount?.shown
      ? permanentDiscount.token ?? undefined
      : undefined
    const result = await createContractMutation.mutateAsync({
      language,
      market,
      discountToken,
      permanentDiscount: permanentDiscount
        ? permanentDiscountTrackingSchema.parse(permanentDiscount)
        : undefined,
    })

    if (!result.success) {
      handlePurchaseFailure(result)
      setPurchasing(false)
      return false
    }

    storeReferralResult(result)
    return true
  }

  const handlePurchaseFailure = (result: {
    success: boolean
    error?: string
    otpRequired?: boolean
  }) => {
    if (result.otpRequired && typeof flowValues.email === 'string') {
      otpSentTo.current = flowValues.email
      otp.requestCode(flowValues.email)
      return
    }
    const message = result.error || t('sharedFlows_common:errors.common')
    track(EVENTS.ErrorMessageShown, {
      errorMessage: message,
      currentStepName: 'payment',
      product_id: config.productKey,
    })
    setPurchaseError(message)
  }

  const onSubmit = async (values: Record<string, unknown> = {}) => {
    if (backBtnPressed) {
      setBackBtnPressed(false)
    }

    if (stepFlags?.isEmailStep && typeof values.email === 'string' && values.email) {
      pendingSubmitValues.current = values
      const otpNeeded = await otp.runEmailCheck(values.email)
      if (otpNeeded) {
        return
      }
      pendingSubmitValues.current = null
    }

    if (props.stepName === 'payment') {
      const purchased = await runPurchase()
      if (!purchased) {
        return
      }
    }

    return submitStep(values ?? {})
  }

  const [navigating, setNavigating] = useState(false)

  useEffect(() => {
    setNavigating(false)
    mutation.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `mutation` is a stable ref from tRPC; adding it would reset on every render
  }, [props.stepName])

  const redirectContext = useRef({
    appliedCoupon,
    flowValues,
    latestPriceData,
    locale: props.locale,
    stepFlags,
    whitelistedParams,
  })
  redirectContext.current = {
    appliedCoupon,
    flowValues,
    latestPriceData,
    locale: props.locale,
    stepFlags,
    whitelistedParams,
  }

  const { data: submitData, isSuccess: submitSucceeded, reset: resetSubmit } = mutation

  useEffect(() => {
    if (!submitSucceeded || !isMatching({ redirectURL: P.string }, submitData)) {
      return
    }
    const { redirectURL } = submitData
    const context = redirectContext.current

    if (context.stepFlags?.isQuoteStep) {
      const trackingValues = extractTrackingFlowValues(context.flowValues)
      const getVariant = config.getTrackingVariant ?? getTariffLine
      track(
        EVENTS.ProductAdded,
        buildProductAddedPayload({
          market,
          premiumInterval: getPremiumIntervalFromValues(trackingValues),
          priceData: context.latestPriceData,
          productKey: config.productKey,
          variant: getVariant(trackingValues, config.productKey),
          coupon: context.appliedCoupon,
        })
      )
    }

    setNavigating(true)
    resetSubmit()
    void push(getPathWithParams(redirectURL, { params: context.whitelistedParams }), undefined, {
      locale: context.locale,
    })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => setNavigating(false))
  }, [config, market, push, resetSubmit, submitData, submitSucceeded, track])

  return {
    airpodsNotice,
    backBtnPressed,
    checkingEmail: checkEmailMutation.isPending,
    setBackBtnPressed,
    mutation,
    navigating,
    onSubmit,
    otp,
    purchaseError,
    purchasing,
  }
}
